import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import { defineComponent as _defineComponent } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { toNumber } from 'lodash';
import { GChart } from 'vue-google-charts';
import { editablePrecontractualReportAdditionalInfo, editablePrecontractualReportForm } from '~/assets/fund/precontractualReport';
import { renderMarkdown } from '~/functions/markdown';
import { getNestedProperty } from '~/functions/sfdr';
import updateFundMutation from '~/graphql/mutations/account/UpdateFund.gql';
import updatePrecontractualReportMutation from '~/graphql/mutations/account/UpdatePrecontractualReport.gql';
import { useGetFundQuery, useGetFundReportQuery } from '~/types/types';
import { getLocale } from '~/functions/translate';
export default /*#__PURE__*/_defineComponent({
  __name: 'precontractual',
  setup: function setup(__props) {
    var route = useRoute();
    var _useGetFundQuery = useGetFundQuery({
        id: toNumber(route.params.fund)
      }),
      result = _useGetFundQuery.result;
    var fund = computed(function () {
      var _result$value;
      return (_result$value = result.value) === null || _result$value === void 0 ? void 0 : _result$value.getFund;
    });
    var getFundReportQueryVariables = computed(function () {
      var _fund$value$id, _fund$value, _route$query;
      return {
        input: {
          id: (_fund$value$id = fund === null || fund === void 0 ? void 0 : (_fund$value = fund.value) === null || _fund$value === void 0 ? void 0 : _fund$value.id) !== null && _fund$value$id !== void 0 ? _fund$value$id : 0,
          referencePeriodId: toNumber((_route$query = route.query) === null || _route$query === void 0 ? void 0 : _route$query.referencePeriodId)
        }
      };
    });
    var _useGetFundReportQuer = useGetFundReportQuery(getFundReportQueryVariables, computed(function () {
        var _route$query2;
        return {
          enabled: !!((_route$query2 = route.query) !== null && _route$query2 !== void 0 && _route$query2.referencePeriodId)
        };
      })),
      calculatedSfdrReportResult = _useGetFundReportQuer.result;
    var calculatedSfdrReport = computed(function () {
      var _calculatedSfdrReport;
      return (calculatedSfdrReportResult === null || calculatedSfdrReportResult === void 0 ? void 0 : (_calculatedSfdrReport = calculatedSfdrReportResult.value) === null || _calculatedSfdrReport === void 0 ? void 0 : _calculatedSfdrReport.getFundReport) || {
        nameOfFinancialMarketParticipant: '',
        legalEntityIdentifier: ''
      };
    });
    var _useMutation = useMutation(updatePrecontractualReportMutation),
      updatePrecontractualReport = _useMutation.mutate;
    var _useMutation2 = useMutation(updateFundMutation),
      updateFund = _useMutation2.mutate,
      updateFundLoading = _useMutation2.loading;
    var calculatedPercentageQuestionKeys = computed(function () {
      if (calculatedSfdrReport.value.isArticle9) {
        return [{
          key: 'environmentalObjectiveCalculated',
          percentage: calculatedSfdrReport.value.percentSustainableInvestmentsWithEnvironmentalObjective
        }, {
          key: 'socialObjectiveCalculated',
          percentage: calculatedSfdrReport.value.percentSustainableInvestmentsWithSocialObjective
        }];
      } else {
        return [{
          key: 'esCharacteristicCalculated',
          percentage: calculatedSfdrReport.value.percentSustainableInvestmentsWhilePromotingEsCharacteristics
        }];
      }
    });
    var investments = ref({
      selected: [],
      headers: [{
        text: 'Largest investments',
        value: 'investment',
        sortable: true,
        align: 'center'
      }, {
        text: 'Sector',
        value: 'sector',
        sortable: true,
        align: 'center'
      }, {
        text: '% Assets',
        value: 'percentAssets',
        sortable: true,
        align: 'center'
      }, {
        text: 'Country',
        value: 'country',
        sortable: true,
        align: 'center'
      }]
    });
    var showCalculatedPercentage = ref(false);
    var toggleShowCalculatedPercentage = function toggleShowCalculatedPercentage() {
      showCalculatedPercentage.value = !showCalculatedPercentage.value;
    };
    var isConsideringPais = computed(function () {
      var _fund$value$isReporti, _fund$value2;
      return (_fund$value$isReporti = fund === null || fund === void 0 ? void 0 : (_fund$value2 = fund.value) === null || _fund$value2 === void 0 ? void 0 : _fund$value2.isReportingPais) !== null && _fund$value$isReporti !== void 0 ? _fund$value$isReporti : false;
    });
    var triggerUpdateFund = function triggerUpdateFund(key, val) {
      var _fund$value3;
      var input = {
        id: (_fund$value3 = fund.value) === null || _fund$value3 === void 0 ? void 0 : _fund$value3.id
      };
      input[key] = val === undefined || val === '' ? null : val;
      updateFund({
        input: input
      });
    };
    var triggerUpdatePrecontractualReport = function triggerUpdatePrecontractualReport(key, val) {
      var _fund$value4;
      var input = {
        id: (_fund$value4 = fund.value) === null || _fund$value4 === void 0 ? void 0 : _fund$value4.id,
        precontractualReport: _defineProperty({}, key, val === undefined || val === '' ? null : val)
      };
      updatePrecontractualReport({
        input: input
      });
    };
    var locale = computed(function () {
      return getLocale();
    });

    // Static data and functions that don't need to be reactive
    var form = editablePrecontractualReportForm;
    var investmentsCategorisationChartOptions = {
      packages: ['orgchart'],
      width: 800,
      height: 600
    };
    return {
      __sfc: true,
      route: route,
      result: result,
      fund: fund,
      getFundReportQueryVariables: getFundReportQueryVariables,
      calculatedSfdrReportResult: calculatedSfdrReportResult,
      calculatedSfdrReport: calculatedSfdrReport,
      updatePrecontractualReport: updatePrecontractualReport,
      updateFund: updateFund,
      updateFundLoading: updateFundLoading,
      calculatedPercentageQuestionKeys: calculatedPercentageQuestionKeys,
      investments: investments,
      showCalculatedPercentage: showCalculatedPercentage,
      toggleShowCalculatedPercentage: toggleShowCalculatedPercentage,
      isConsideringPais: isConsideringPais,
      triggerUpdateFund: triggerUpdateFund,
      triggerUpdatePrecontractualReport: triggerUpdatePrecontractualReport,
      locale: locale,
      form: form,
      investmentsCategorisationChartOptions: investmentsCategorisationChartOptions,
      GChart: GChart,
      editablePrecontractualReportAdditionalInfo: editablePrecontractualReportAdditionalInfo,
      renderMarkdown: renderMarkdown,
      getNestedProperty: getNestedProperty
    };
  }
});